// src/contexts/AppContext.tsx
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';
import type { Server, Friend, Channel, Profile, ServerInvite, UIMessage, Message, Reaction } from '../types';

type DiscoverServer = Server & {
  members_count: number;
  is_member: boolean;
  request_sent: boolean;
  owner_profile: Pick<Profile, 'username'> | null;
};

interface AppData {
    servers: Server[];
    friends: Friend[];
    pendingFriends: Friend[];
    serverInvites: ServerInvite[];
    channels: Channel[];
    allMembers: Map<string, Profile[]>;
    profiles: Map<string, Profile>;
    messages: Map<string, UIMessage[]>;
    discoverableServers: DiscoverServer[];
    adminData: { stats: any; allUsers: Profile[] } | null;
}

interface AppContextType {
    appData: AppData;
    isReady: boolean;
    refetchAll: () => void;
    refetchServers: () => void; // Kept for compatibility, now calls refetchAll
    refetchFriends: () => void; // Kept for compatibility, now calls refetchAll
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const initialAppData: AppData = {
    servers: [],
    friends: [],
    pendingFriends: [],
    serverInvites: [],
    channels: [],
    allMembers: new Map(),
    profiles: new Map(),
    messages: new Map(),
    discoverableServers: [],
    adminData: null,
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [appData, setAppData] = useState<AppData>(initialAppData);
    const [isReady, setIsReady] = useState(false);
    const [refetchCounter, setRefetchCounter] = useState(0);

    const refetchAll = useCallback(() => {
        setRefetchCounter(c => c + 1);
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            if (!user) {
                setAppData(initialAppData);
                setIsReady(false);
                return;
            }

            setIsReady(false);

            try {
                console.log('// AppContext: Kapsamlı ön-yükleme başlatıldı...');
                const [
                    membershipsRes,
                    friendshipsRes,
                    invitesRes,
                ] = await Promise.all([
                    supabase.from('memberships').select('server_id').eq('user_id', user.id),
                    supabase.from('friendships').select('*, user1:profiles!friendships_user1_fk(*), user2:profiles!friendships_user2_fk(*)').or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`),
                    supabase.from('server_invites').select('*').eq('invitee_id', user.id).eq('status', 'pending'),
                ]);

                if (membershipsRes.error) throw membershipsRes.error;
                if (friendshipsRes.error) throw friendshipsRes.error;
                if (invitesRes.error) console.warn('invites fetch failed');
                
                const serverIds = membershipsRes.data?.map(m => m.server_id) || [];
                
                let servers: Server[] = [];
                let channels: Channel[] = [];
                let allMembers = new Map<string, Profile[]>();
                const profilesMap = new Map<string, Profile>();
                let messagesByChannelId = new Map<string, UIMessage[]>();
                let adminData: AppData['adminData'] = null;

                if (serverIds.length > 0) {
                     const [serversRes, channelsRes, allMembershipsRes] = await Promise.all([
                        supabase.from('servers').select('*').in('id', serverIds),
                        supabase.from('channels').select('*').in('server_id', serverIds),
                        supabase.from('memberships').select('server_id, user_id').in('server_id', serverIds)
                     ]);
                     if (serversRes.error) throw serversRes.error;
                     if (channelsRes.error) throw channelsRes.error;
                     if (allMembershipsRes.error) throw allMembershipsRes.error;

                     servers = serversRes.data || [];
                     channels = channelsRes.data || [];

                     const allMemberIds = [...new Set(allMembershipsRes.data.map(m => m.user_id))];
                     if (allMemberIds.length > 0) {
                         const { data: profilesData, error: profilesError } = await supabase.from('profiles').select('*').in('id', allMemberIds);
                         if (profilesError) throw profilesError;
                         (profilesData || []).forEach(p => profilesMap.set(p.id, p as Profile));
                         
                         const membersByServer = new Map<string, Profile[]>();
                         (allMembershipsRes.data || []).forEach(m => {
                             const profile = profilesMap.get(m.user_id);
                             if (profile) {
                                 if (!membersByServer.has(m.server_id)) membersByServer.set(m.server_id, []);
                                 membersByServer.get(m.server_id)!.push(profile);
                             }
                         });
                         allMembers = membersByServer;
                     }
                    
                    const textChannelIds = channels.filter(c => c.type === 'text').map(c => c.id);
                    if (textChannelIds.length > 0) {
                        const messagePromises = textChannelIds.map(id => supabase.from('messages').select(`*`).eq('channel_id', id).order('created_at', { ascending: false }).limit(30));
                        const messageResults = await Promise.all(messagePromises);

                        const allMessages: Message[] = messageResults.flatMap(res => res.data || []);
                        const messageIds = allMessages.map(m => m.id);
                        
                        const messageUserIds = new Set(allMessages.map(m => m.user_id));
                        const newProfileIdsToFetch = [...messageUserIds].filter(id => !profilesMap.has(id));
                        
                        if (newProfileIdsToFetch.length > 0) {
                            const { data: newProfiles } = await supabase.from('profiles').select('*').in('id', newProfileIdsToFetch);
                            (newProfiles || []).forEach(p => profilesMap.set(p.id, p as Profile));
                        }

                        const { data: reactionsData } = await supabase.from('message_reactions').select(`*, profiles(id, username)`).in('message_id', messageIds);
                        const reactionsByMessageId = new Map<string, Reaction[]>();
                        (reactionsData || []).forEach(r => {
                            if (!reactionsByMessageId.has(r.message_id)) reactionsByMessageId.set(r.message_id, []);
                            reactionsByMessageId.get(r.message_id)!.push(r as Reaction);
                        });

                        allMessages.forEach(msg => {
                            if (!messagesByChannelId.has(msg.channel_id)) messagesByChannelId.set(msg.channel_id, []);
                            const completeMessage: UIMessage = { ...msg, profiles: profilesMap.get(msg.user_id) || null, reactions: reactionsByMessageId.get(msg.id) || [], status: 'sent' };
                            messagesByChannelId.get(msg.channel_id)!.push(completeMessage);
                        });
                        messagesByChannelId.forEach((messages) => messages.reverse());
                    }
                }
                
                if (user.email === 'kaankaramann55@gmail.com') {
                     const { data, error } = await supabase.functions.invoke('get-admin-stats');
                     if (error || data.error) console.error("Admin data fetch failed:", error || data.error);
                     else adminData = data;
                }

                const { data: discoverData } = await supabase.from('discoverable_servers').select('*');
                const userServerIds = new Set(serverIds);
                const { data: userInvites } = await supabase.from('server_invites').select('server_id').eq('invitee_id', user.id).eq('status', 'pending').eq('type', 'join_request');
                const pendingRequestServerIds = new Set((userInvites || []).map(i => i.server_id));
                const discoverRows: any[] = (discoverData as any[] || []);
                const ownerIds = [...new Set(discoverRows.map(s => s.owner_id).filter((id: string | null) => !!id))];
                const ownerProfilesMap = new Map<string, { username: string }>();
                if (ownerIds.length > 0) {
                    const { data: ownerProfiles } = await supabase.from('profiles').select('id, username').in('id', ownerIds);
                    (ownerProfiles || []).forEach(p => ownerProfilesMap.set(p.id as string, { username: p.username as string }));
                }
                const discoverableServers: DiscoverServer[] = discoverRows.map(s => ({ 
                    ...s, 
                    is_member: userServerIds.has(s.id), 
                    request_sent: pendingRequestServerIds.has(s.id),
                    owner_profile: (s as any).is_official ? { username: 'SARMAŞIK' } : (s.owner_id && ownerProfilesMap.has(s.owner_id) ? ownerProfilesMap.get(s.owner_id)! : null),
                }));
                
                const acceptedFriends: Friend[] = [];
                const pendingFriends: Friend[] = [];
                (friendshipsRes.data || []).forEach(f => {
                    const friendProfile = f.user1_id === user.id ? f.user2 : f.user1;
                    if (!friendProfile) return;
                    const friendObject: Friend = { ...(friendProfile as Profile), friendship_id: f.id, friendship_status: f.status, action_user_id: f.action_user_id };

                    if (f.status === 'accepted') acceptedFriends.push(friendObject);
                    else if (f.status === 'pending' && f.action_user_id !== user.id) pendingFriends.push(friendObject);
                });
                
                const serverInvites: ServerInvite[] = [];

                setAppData({ servers, friends: acceptedFriends, pendingFriends, serverInvites, channels, allMembers, profiles: profilesMap, messages: messagesByChannelId, discoverableServers, adminData });
                console.log('// AppContext: Kapsamlı ön-yükleme tamamlandı. Uygulama hazır.');
            } catch (error) {
                console.error("AppContext: Uygulama verileri başlatılamadı:", error);
                setAppData(initialAppData);
            } finally {
                setIsReady(true);
            }
        };

        fetchAllData();
    }, [user?.id, refetchCounter]);

    useEffect(() => {
        if (!user) return;
        const ids: string[] = [];
        appData.allMembers.forEach(list => list.forEach(p => { ids.push(p.id); }));
        const uniqueIds = Array.from(new Set(ids));
        if (uniqueIds.length === 0) return;
        const filter = `id=in.(${uniqueIds.join(',')})`;
        const sub = supabase
            .channel(`profiles-status-${user.id}`)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter }, (payload) => {
                const updated = payload.new as Profile;
                setAppData(prev => {
                    const newAllMembers = new Map(prev.allMembers);
                    newAllMembers.forEach((arr, sid) => {
                        const idx = arr.findIndex(p => p.id === updated.id);
                        if (idx !== -1) {
                            const nextArr = arr.slice();
                            nextArr[idx] = updated as Profile;
                            newAllMembers.set(sid, nextArr);
                        }
                    });
                    const newProfiles = new Map(prev.profiles);
                    newProfiles.set(updated.id, updated as Profile);
                    return { ...prev, allMembers: newAllMembers, profiles: newProfiles };
                });
            })
            .subscribe();
        return () => { supabase.removeChannel(sub); };
    }, [user?.id, appData.allMembers]);

    useEffect(() => {
        const ch = supabase.channel('presence-users', { config: { presence: { key: user?.id || 'observer' } } });
        ch.subscribe(() => {});
        ch.on('presence', { event: 'sync' }, () => {
            const state: Record<string, any[]> = (ch as any).presenceState();
            const onlineIds = new Set<string>(Object.keys(state));
            setAppData(prev => {
                const updated = new Map<string, Profile[]>();
                prev.allMembers.forEach((arr, sid) => {
                    updated.set(sid, arr.map(p => onlineIds.has(p.id) ? p : { ...p, status: 'offline' }));
                });
                return { ...prev, allMembers: updated };
            });
        });
        return () => { supabase.removeChannel(ch); };
    }, [user?.id]);

    const value = useMemo(() => ({
        appData,
        isReady,
        refetchAll,
        refetchServers: refetchAll,
        refetchFriends: refetchAll,
    }), [appData, isReady, refetchAll]);

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
